import {
  AlertTriangleIcon,
  TrendingUpIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Progress } from "@/components/ui/progress"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import {
  calculateProgressSeverity,
  formatVolumeProgress,
  getProgressSeverityColor,
} from "@/utils/smart-routing-alerts"

interface ProgressIndicatorProps {
  processor: string
  volume: number | null
  target: number | null
  progress: number | null
  fixed: boolean
  className?: string
}

export const ProgressIndicator = ({
  processor,
  volume,
  target,
  progress,
  fixed,
  className,
}: ProgressIndicatorProps) => {
  const { t } = useTranslation()

  if (fixed) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2">
          <p className="font-medium">{processor}</p>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {t("smartRouting.table.fixed")}
          </span>
        </div>
      </div>
    )
  }

  const severity = calculateProgressSeverity(progress)
  const color = getProgressSeverityColor(severity)
  const progressValue = progress ?? 0
  const volumeProgressText = formatVolumeProgress(
    volume,
    target,
    progress
  )

  const getAlertIcon = () => {
    if (severity === "critical") {
      return (
        <AlertTriangleIcon
          size={14}
          className="text-red-600"
        />
      )
    }
    if (severity === "warning" && progressValue > 100) {
      return (
        <TrendingUpIcon
          size={14}
          className="text-amber-600"
        />
      )
    }
    return null
  }

  return (
    <div className={className}>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <p className="font-medium">{processor}</p>
          {getAlertIcon()}
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-help space-y-0.5">
              <Progress
                value={Math.min(progressValue, 200)} // Cap at 200% for visual
                className={cn(
                  "h-1.5 w-24 sm:w-32",
                  severity === "critical" &&
                    "border-red-200"
                )}
              />
              <p
                className={cn(
                  "text-xs font-medium",
                  severity === "critical" && "text-red-600",
                  severity === "warning" &&
                    "text-amber-600",
                  severity === "normal" &&
                    "text-muted-foreground"
                )}
              >
                {progressValue.toFixed(1)}%
                {progressValue > 100 && (
                  <span className="ml-1 text-xs">
                    ({progressValue > 150 ? "🚨" : "⚠️"})
                  </span>
                )}
              </p>
            </div>
          </TooltipTrigger>
          <TooltipContent
            style={{
              backgroundColor:
                "var(--whitelabel-background)",
            }}
            className="border-0 text-white"
          >
            <div className="space-y-1">
              <p className="font-medium text-white">
                {t("smartRouting.progress.volumeTitle")}
              </p>
              <p className="text-sm text-white">
                {volumeProgressText}
              </p>
              {severity === "critical" && (
                <p className="text-xs text-gray-200">
                  🚨{" "}
                  {t(
                    "smartRouting.progress.criticalMessage"
                  )}
                </p>
              )}
              {severity === "warning" && (
                <p className="text-xs text-gray-200">
                  ⚠️{" "}
                  {t(
                    "smartRouting.progress.warningMessage"
                  )}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      <style>{`
        .progress-bar {
          background-color: ${color};
        }
      `}</style>
    </div>
  )
}
